<template>
  <div
    ref="containerRef"
    class="image-player"
    :class="{
      'image-player--fullscreen': player.isFullscreen.value,
      'image-player--grid': player.layout.value === 'grid',
      'image-player--carousel': player.layout.value === 'carousel',
      'image-player--light': theme === 'light',
    }"
    :style="primaryColorStyle"
    @keydown="handleKeyDown"
    tabindex="0"
  >
    <!-- Toolbar -->
    <ImageToolbar
      :theme="theme"
      :current-index="player.currentIndex.value"
      :total-items="player.totalItems.value"
      :zoom="player.panZoom.value.zoom"
      :max-zoom="config.maxZoom || 4"
      :is-zoomed="player.panZoom.value.isZoomed"
      :is-fullscreen="player.isFullscreen.value"
      :slideshow-active="player.slideshowActive.value"
      :can-prev="player.canGoPrev.value"
      :can-next="player.canGoNext.value"
      :enable-zoom="config.enableZoom !== false"
      :enable-slideshow="config.enableSlideshow !== false && player.totalItems.value > 1"
      :enable-download="config.enableDownload === true"
      :enable-fullscreen="config.enableFullscreen !== false"
      @prev="player.prev"
      @next="player.next"
      @zoom-in="player.zoomIn"
      @zoom-out="player.zoomOut"
      @reset-zoom="player.resetZoom"
      @toggle-slideshow="player.toggleSlideshow"
      @toggle-fullscreen="handleToggleFullscreen"
      @download="handleDownload"
    />

    <!-- Main content area -->
    <div class="image-player__content">
      <!-- Grid layout -->
      <div v-if="player.layout.value === 'grid'" class="image-player__grid">
        <button
          v-for="(item, index) in player.items.value"
          :key="item.asset.uid"
          class="image-player__grid-item"
          :class="{ 'image-player__grid-item--active': index === player.currentIndex.value }"
          @click="handleGridItemClick(index)"
        >
          <!-- A square grid cell, 200–250px wide, cropped by its own `object-fit: cover`. -->
          <img
            v-if="item.asset.thumbnailUrl"
            :src="withThumbnailSize(item.asset.thumbnailUrl, 480, 480, 'cover')"
            :alt="item.customTitle || item.asset.name"
            class="image-player__grid-image"
            loading="lazy"
          />
          <div v-else class="image-player__grid-placeholder">
            <v-icon size="48" color="grey" :icon="mdiImage" />
          </div>
          <span v-if="config.showCaptions" class="image-player__grid-caption">
            {{ item.customTitle || item.asset.name }}
          </span>
        </button>
      </div>

      <!-- Carousel/Fullscreen layout -->
      <template v-else>
        <!-- Viewer area -->
        <div ref="viewerRef" class="image-player__viewer">
          <!-- Loading state -->
          <div v-if="player.isCurrentImageLoading.value && !player.currentImageUrl.value" class="image-player__loading">
            <v-progress-circular indeterminate size="64" width="4" color="primary" />
            <span class="image-player__loading-text">Loading image...</span>
          </div>

          <!-- Error state -->
          <div v-else-if="player.error.value" class="image-player__error">
            <v-icon color="error" size="64" :icon="mdiImageBrokenVariant" />
            <span class="image-player__error-text">
              {{ player.error.value.message }}
            </span>
            <v-btn variant="tonal" color="primary" class="mt-4" @click="handleRetry">
              Retry
            </v-btn>
          </div>

          <!-- Image viewer -->
          <ImageViewer
            v-else
            :src="player.currentImageUrl.value"
            :alt="player.currentAsset.value?.name"
            :pan-zoom="player.panZoom.value"
            :transition="config.transition || 'fade'"
            :transition-duration="config.transitionDuration || 300"
            :enable-zoom="config.enableZoom !== false"
            :enable-pan="config.enableZoom !== false"
            @zoom-in="player.zoomIn"
            @zoom-out="player.zoomOut"
            @reset-zoom="player.resetZoom"
            @pan="handlePan"
            @load="handleImageLoad"
            @error="handleImageError"
          />

          <!-- Navigation arrows (carousel mode) -->
          <template v-if="player.layout.value === 'carousel' && player.totalItems.value > 1">
            <v-btn
              v-if="player.canGoPrev.value"
              class="image-player__nav-btn image-player__nav-btn--prev"
              :icon="mdiChevronLeft"
              variant="flat"
              size="large"
              @click="player.prev"
            />
            <v-btn
              v-if="player.canGoNext.value"
              class="image-player__nav-btn image-player__nav-btn--next"
              :icon="mdiChevronRight"
              variant="flat"
              size="large"
              @click="player.next"
            />
          </template>

          <!-- Asset info card (collection mode) -->
          <div
            v-if="showAssetCard"
            class="image-player__asset-card"
            :class="{ 'image-player__asset-card--clickable': canOpenAsset }"
            @click="canOpenAsset && openAssetInNewTab()"
          >
            <span class="image-player__asset-name">{{ currentDisplayName }}</span>
            <v-icon
              v-if="canOpenAsset"
              :icon="mdiOpenInNew"
              size="14"
              class="image-player__open-icon"
            />
          </div>
        </div>

        <!-- Thumbnail strip (carousel mode) -->
        <ImageThumbnailStrip
          v-if="player.layout.value === 'carousel' && player.totalItems.value > 1"
          :items="thumbnailItems"
          :current-index="player.currentIndex.value"
          :layout="player.layout.value"
          @select="player.goToIndex"
        />
      </template>
    </div>

    <!-- Logo overlay — bottom-right, lifted above thumbnail strip in carousel -->
    <component
      :is="logoClickUrl ? 'a' : 'div'"
      v-if="logoUrl"
      class="image-player__logo"
      :class="{ 'image-player__logo--above-strip': hasThumbnailStrip }"
      :href="logoClickUrl || undefined"
      :target="logoClickUrl ? '_blank' : undefined"
      :rel="logoClickUrl ? 'noopener noreferrer' : undefined"
    >
      <img :src="logoUrl" alt="" class="image-player__logo-img" />
    </component>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted, type PropType, type Ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { mdiChevronLeft, mdiChevronRight, mdiImage, mdiImageBrokenVariant, mdiOpenInNew } from '@mdi/js'
import { useFullscreen, useResizeObserver } from '@vueuse/core'
import type { ImagePlayerConfig, ImagePlayerCallbacks, PanZoomState, ImageLayout } from '../types'
import type { ImagePlaybackInfo, ImageAssetListItem } from '../../types/asset'
import type { PlayerError } from '../../types/base'
import type { CollectionItem } from '../../types/player'
import { useImagePlayer } from '../composables/useImagePlayer'
import { withThumbnailSize } from '../../common/thumbnail'
import { hexToRgb } from '../../audio/utils/audio-utils'
import ImageToolbar from './ImageToolbar.vue'
import ImageViewer from './ImageViewer.vue'
import ImageThumbnailStrip from './ImageThumbnailStrip.vue'

const props = defineProps({
  config: {
    type: Object as PropType<ImagePlayerConfig>,
    required: true,
  },
  playbackInfo: {
    type: Object as PropType<ImagePlaybackInfo>,
    default: undefined,
  },
  playbackInfos: {
    type: Array as PropType<ImagePlaybackInfo[]>,
    default: undefined,
  },
  poster: {
    type: String,
    default: undefined,
  },
  theme: {
    type: String as PropType<'dark' | 'light'>,
    default: 'dark',
  },
  primaryColor: {
    type: String,
    default: undefined,
  },
  logoUrl: {
    type: String,
    default: undefined,
  },
  logoPosition: {
    type: String as PropType<'top_left' | 'top_right' | 'bottom_left' | 'bottom_right' | 'control_bar'>,
    default: undefined,
  },
  logoClickUrl: {
    type: String,
    default: undefined,
  },
  // Enables the "open asset in new tab" link on the carousel info card.
  // Only meaningful inside Mediasbox (internal app) — embeds and the Developer
  // preview must keep this disabled.
  enableAssetLink: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  itemChange: [index: number, item: CollectionItem<ImageAssetListItem>]
  layoutChange: [layout: ImageLayout]
  zoomChange: [state: PanZoomState]
  panChange: [panX: number, panY: number]
  slideshowToggle: [active: boolean]
  slideshowPause: [paused: boolean]
  fullscreenChange: [isFullscreen: boolean]
  imageLoad: [assetId: string]
  imageError: [assetId: string, error: PlayerError]
  download: [assetId: string]
  playbackInfoNeeded: [assetId: string]
  error: [error: PlayerError]
  /** The current picture is decoded and on screen — not its playback info, which `imageLoad` is. */
  displayed: [assetId: string]
}>()

// Refs
const containerRef: Ref<HTMLElement | null> = ref(null)
const viewerRef: Ref<HTMLElement | null> = ref(null)

// Override --v-theme-primary with the caller-provided primary color so CSS
// rules like `rgb(var(--v-theme-primary))` pick up the embed theme.
const primaryColorStyle = computed<Record<string, string> | undefined>(() => {
  if (!props.primaryColor) return undefined
  const rgb = hexToRgb(props.primaryColor)
  if (!rgb) return undefined
  return { '--v-theme-primary': `${rgb[0]}, ${rgb[1]}, ${rgb[2]}` }
})


// Fullscreen
const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(containerRef)

// Build initial playback infos map
function buildInitialPlaybackInfos(): Map<string, ImagePlaybackInfo> | undefined {
  // If playbackInfos array is provided, use it
  if (props.playbackInfos && props.playbackInfos.length > 0) {
    const map = new Map<string, ImagePlaybackInfo>()
    for (const info of props.playbackInfos) {
      map.set(info.uid, info)
    }
    return map
  }
  // Fallback to single playbackInfo for backwards compatibility
  if (props.playbackInfo && props.config.items?.[0]) {
    return new Map([[props.config.items[0].asset.uid, props.playbackInfo]])
  }
  return undefined
}

// Initialize player
const player = useImagePlayer({
  config: props.config,
  initialPlaybackInfos: buildInitialPlaybackInfos(),
  callbacks: {
    onItemChange: (index: number, item: CollectionItem<ImageAssetListItem>) => {
      emit('itemChange', index, item)
    },
    onLayoutChange: (layout: ImageLayout) => {
      emit('layoutChange', layout)
    },
    onZoomChange: (state: PanZoomState) => {
      emit('zoomChange', state)
    },
    onPanChange: (panX: number, panY: number) => {
      emit('panChange', panX, panY)
    },
    onSlideshowToggle: (active: boolean) => {
      emit('slideshowToggle', active)
    },
    onSlideshowPause: (paused: boolean) => {
      emit('slideshowPause', paused)
    },
    onFullscreenChange: (fs: boolean) => {
      emit('fullscreenChange', fs)
    },
    onImageLoad: (assetId: string) => {
      emit('imageLoad', assetId)
    },
    onImageError: (assetId: string, error: PlayerError) => {
      emit('imageError', assetId, error)
    },
    onDownload: (assetId: string) => {
      emit('download', assetId)
    },
    onError: (error: PlayerError) => {
      emit('error', error)
    },
    // Use callback from config if provided (for ImageEmbed integration)
    onPlaybackInfoNeeded: props.config.callbacks?.onPlaybackInfoNeeded,
  },
})

// Extract ImageAssetListItem from CollectionItem for ThumbnailStrip
const thumbnailItems = computed(() => player.items.value.map((item) => item.asset))

// The logo is pinned bottom-right; in carousel mode with multiple items, it
// must sit above the thumbnail strip instead of overlapping it.
const hasThumbnailStrip = computed(() =>
  player.layout.value === 'carousel' && player.totalItems.value > 1,
)

// Asset info overlay
const router = useRouter()
const route = useRoute()

const currentDisplayName = computed(() => {
  const item = player.currentItem.value
  if (!item) return ''
  return item.customTitle || item.asset.name
})

const canOpenAsset = computed(() => {
  if (!props.enableAssetLink) return false
  if (player.totalItems.value <= 1 || !player.currentAsset.value) return false
  try {
    return router.hasRoute('assets-id') && !!route.params.organizationId
  } catch {
    return false
  }
})

// Title card is shown in carousel mode with multiple items when captions are
// enabled. The openInNew icon inside the card is gated by `canOpenAsset`.
const showAssetCard = computed(() => {
  if (player.layout.value !== 'carousel') return false
  if (player.totalItems.value <= 1 || !player.currentAsset.value) return false
  return props.config.showCaptions !== false
})

function openAssetInNewTab(): void {
  const asset = player.currentAsset.value
  if (!asset || !canOpenAsset.value) return
  const resolved = router.resolve({
    name: 'assets-id',
    params: {
      organizationId: route.params.organizationId as string,
      assetId: asset.uid,
    },
  })
  window.open(resolved.href, '_blank')
}

// Event handlers
function handleToggleFullscreen(): void {
  toggleFullscreen()
}

function handleDownload(): void {
  player.download()
}

function handlePan(deltaX: number, deltaY: number): void {
  player.applyPanDelta(deltaX, deltaY)
}

function handleImageLoad(dimensions: { width: number; height: number }): void {
  player.setImageDimensions(dimensions)
  // The viewer's `<img>` loaded: the picture is shown, which is when an image ends
  // (`../../common/stillEnd.ts`).
  const asset = player.currentAsset.value
  if (asset) emit('displayed', asset.uid)
}

function handleImageError(error: Error): void {
  const currentAsset = player.currentAsset.value
  if (currentAsset) {
    player.error.value = {
      code: 'PLAYBACK_ERROR',
      message: error.message,
      originalError: error,
      recoverable: true,
    }
  }
}

function handleRetry(): void {
  player.clearError()
  const currentAsset = player.currentAsset.value
  if (currentAsset) {
    player.requestPlaybackInfo(currentAsset.uid)
  }
}

function handleGridItemClick(index: number): void {
  player.goToIndex(index)
  // Switch to fullscreen/carousel mode when clicking a grid item
  if (props.config.layout === 'grid') {
    player.setLayout('fullscreen')
  }
}

function handleKeyDown(event: KeyboardEvent): void {
  switch (event.key) {
    case 'ArrowLeft':
      event.preventDefault()
      player.prev()
      break
    case 'ArrowRight':
      event.preventDefault()
      player.next()
      break
    case 'Escape':
      event.preventDefault()
      if (player.isFullscreen.value) {
        toggleFullscreen()
      } else if (player.panZoom.value.isZoomed) {
        player.resetPanZoom()
      } else if (player.layout.value === 'fullscreen' && props.config.layout === 'grid') {
        player.setLayout('grid')
      }
      break
    case '+':
    case '=':
      event.preventDefault()
      player.zoomIn()
      break
    case '-':
      event.preventDefault()
      player.zoomOut()
      break
    case ' ':
      event.preventDefault()
      if (player.slideshowActive.value) {
        if (player.slideshowPaused.value) {
          player.resumeSlideshow()
        } else {
          player.pauseSlideshow()
        }
      } else {
        player.toggleSlideshow()
      }
      break
    case 'Home':
      event.preventDefault()
      player.first()
      break
    case 'End':
      event.preventDefault()
      player.last()
      break
  }
}

// Resize observer for container
useResizeObserver(viewerRef, (entries) => {
  const entry = entries[0]
  if (entry) {
    player.updateContainerSize({
      width: entry.contentRect.width,
      height: entry.contentRect.height,
    })
  }
})

// Watch fullscreen state sync
watch(isFullscreen, (value) => {
  if (value !== player.isFullscreen.value) {
    player.toggleFullscreen()
  }
})

// Watch for external playback info (single)
watch(
  () => props.playbackInfo,
  (newInfo) => {
    if (newInfo) {
      player.setPlaybackInfo(newInfo.uid, newInfo)
    }
  },
)

// Watch for external playback infos (array)
watch(
  () => props.playbackInfos,
  (newInfos) => {
    if (newInfos && newInfos.length > 0) {
      for (const info of newInfos) {
        player.setPlaybackInfo(info.uid, info)
      }
    }
  },
  { deep: true },
)

// No `focus()` on mount: the player is mounted inside somebody else's page, and focusing on load
// scrolls that page to the iframe and takes its keyboard. The container is `tabindex="0"`, so a
// click or a Tab into it gives its key handler focus — as the video and audio players do.

// Cleanup
onUnmounted(() => {
  player.destroy()
})

// Expose player methods for external control
defineExpose({
  player,
  goToIndex: player.goToIndex,
  next: player.next,
  prev: player.prev,
  zoomIn: player.zoomIn,
  zoomOut: player.zoomOut,
  resetZoom: player.resetZoom,
  toggleSlideshow: player.toggleSlideshow,
  setPlaybackInfo: player.setPlaybackInfo,
})
</script>

<style lang="scss" scoped>

.image-player {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #1a1a1a;
  overflow: hidden;
  outline: none;

  &--light {
    background: #f5f5f5;
  }

  &--fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
  }

  &__content {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
  }

  &__viewer {
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: #1a1a1a;

    .image-player--light & {
      background: #f5f5f5;
    }
  }

  &__loading,
  &__error {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    color: white;
    text-align: center;

    .image-player--light & {
      color: #333;
    }
  }

  &__loading-text,
  &__error-text {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
    max-width: 300px;

    .image-player--light & {
      color: rgba(0, 0, 0, 0.6);
    }
  }

  &__nav-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    background-color: rgba(0, 0, 0, 0.5) !important;
    color: white !important;
    opacity: 0.8;
    transition: opacity 0.2s ease, background-color 0.2s ease;

    .image-player--light & {
      background-color: rgba(0, 0, 0, 0.08) !important;
      color: #333 !important;
    }

    &:hover {
      opacity: 1;
      background-color: rgba(0, 0, 0, 0.7) !important;
    }

    .image-player--light &:hover {
      background-color: rgba(0, 0, 0, 0.15) !important;
    }

    &--prev {
      left: 16px;
    }

    &--next {
      right: 16px;
    }
  }

  &__asset-card {
    position: absolute;
    bottom: 12px;
    left: 16px;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 12px;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(8px);
    border-radius: 9999px;
    z-index: 10;
    // Leave room for the bottom-right logo overlay (max ~120px + padding).
    max-width: calc(100% - 160px);
    transition: background 150ms ease;

    .image-player--light & {
      background: rgba(255, 255, 255, 0.85);
    }

    &--clickable {
      cursor: pointer;

      &:hover {
        background: rgba(0, 0, 0, 0.85);
      }

      .image-player--light &:hover {
        background: rgba(255, 255, 255, 0.95);
      }
    }
  }

  &__asset-name {
    font-size: 0.75rem;
    font-weight: 500;
    color: white;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    flex: 1 1 auto;

    .image-player--light & {
      color: #333;
    }
  }

  &__open-icon {
    flex-shrink: 0;
    color: rgba(255, 255, 255, 0.7);

    .image-player--light & {
      color: rgba(0, 0, 0, 0.5);
    }
  }

  // Grid layout
  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
    padding: 16px;
    overflow-y: auto;
    width: 100%;

    @media (min-width: 768px) {
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    }
  }

  &__grid-item {
    position: relative;
    aspect-ratio: 1;
    border: 2px solid transparent;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.05);
    transition: border-color 0.2s ease, transform 0.2s ease;
    padding: 0;
    display: flex;
    flex-direction: column;

    .image-player--light & {
      background: rgba(0, 0, 0, 0.04);
    }

    &:hover {
      border-color: rgba(var(--v-theme-primary), 0.5);
      transform: scale(1.02);
    }

    &--active {
      border-color: rgb(var(--v-theme-primary));
    }
  }

  &__grid-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__grid-placeholder {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.05);

    .image-player--light & {
      background: rgba(0, 0, 0, 0.04);
    }
  }

  &__grid-caption {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 8px;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
    color: white;
    font-size: 12px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;

    .image-player--light & {
      background: linear-gradient(transparent, rgba(0, 0, 0, 0.6));
    }
  }

  &__logo {
    position: absolute;
    right: 12px;
    bottom: 12px;
    z-index: 10;
    pointer-events: auto;
    line-height: 0;
    transition: opacity 0.3s ease, bottom 0.2s ease;

    &--above-strip {
      bottom: 92px;
    }

    &:focus-visible {
      outline: 2px solid rgba(255, 255, 255, 0.8);
      outline-offset: 2px;
      border-radius: 4px;
    }
  }

  &__logo-img {
    max-height: 40px;
    max-width: 120px;
    object-fit: contain;
    display: block;
  }
}
</style>
